/**
 * Date utilities for CST timezone handling
 * All dates in the portal should be displayed in CST (Central Standard Time)
 */

// CST timezone identifier
const CST_TIMEZONE = 'America/Chicago';

/**
 * Format a date for display in CST timezone
 * @param {string|Date} date - The date to format
 * @param {object} options - Additional options for toLocaleDateString
 * @returns {string} Formatted date string
 */
export const formatDateCST = (date, options = {}) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { timeZone: CST_TIMEZONE, ...options });
};

/**
 * Format a date with time for display in CST timezone
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeCST = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-US', { 
    timeZone: CST_TIMEZONE,
    dateStyle: 'short',
    timeStyle: 'short'
  });
};

/**
 * Get today's date in YYYY-MM-DD format (CST)
 * Use this for setting default values in date inputs
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayCST = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: CST_TIMEZONE });
};

/**
 * Convert a date from the database to YYYY-MM-DD format for date inputs
 * @param {string|Date} date - The date from the database
 * @returns {string} Date in YYYY-MM-DD format, or empty string if invalid
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: CST_TIMEZONE });
};

/**
 * Convert a date input value (YYYY-MM-DD) to ISO string for sending to backend
 * Adds noon time to avoid date shifting due to timezone differences
 * @param {string} dateInputValue - The value from a date input (YYYY-MM-DD)
 * @returns {string|null} ISO string or null if empty
 */
export const dateInputToISO = (dateInputValue) => {
  if (!dateInputValue) return null;
  // Add noon time to avoid timezone date shifting
  return new Date(dateInputValue + 'T12:00:00').toISOString();
};

/**
 * Format a relative date (e.g., "2 days ago", "in 3 months")
 * @param {string|Date} date - The date to format
 * @returns {string} Relative date string
 */
export const formatRelativeDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  
  const now = new Date();
  const diffMs = d - now;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  
  return formatDateCST(date);
};

/**
 * Check if a date is expired (past today in CST)
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if expired
 */
export const isExpired = (date) => {
  if (!date) return false;
  const d = new Date(date);
  const today = new Date(getTodayCST() + 'T00:00:00');
  return d < today;
};

/**
 * Check if a date is expiring soon (within specified days)
 * @param {string|Date} date - The date to check
 * @param {number} days - Number of days threshold (default: 30)
 * @returns {boolean} True if expiring soon
 */
export const isExpiringSoon = (date, days = 30) => {
  if (!date) return false;
  const d = new Date(date);
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + days);
  return d <= threshold && !isExpired(date);
};

export default {
  formatDateCST,
  formatDateTimeCST,
  getTodayCST,
  formatDateForInput,
  dateInputToISO,
  formatRelativeDate,
  isExpired,
  isExpiringSoon,
  CST_TIMEZONE,
};

